import { useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { AlertTriangle, CheckCircle2, XCircle, Clock, Plus, Send, ChevronDown, ChevronUp, FileText, Upload, Phone, Mail, Building2, User as UserIcon } from "lucide-react";
import { openPendingFile } from "../../../utils/download";
import { formatAmount } from "@/utils/formatAmount";
import { displayUnit } from "@/utils/displayUnit";
import { useOrder, usePenaltyCalc, useAppliedPenalties, useContractorOrderPreview } from "../hooks/useOrders";
import { ContractorPreviewProvider, useContractorPreview } from "../contractorPreview";
import { OrderGrantedStaffCard } from "@/features/orders/components/OrderGrantedStaffCard";
import { ExecutorPockSection } from "@/features/orders/components/ExecutorPockSection";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { ORDER_STATUS_LABELS, PRIORITY_LABELS, POCK_REQUEST_STATUS_LABELS, WEEKLY_REPORT_STATUS_LABELS, type OrderStatus, type PockRequestStatus, type WeeklyReportStatus } from "../types";
import { OrderAttachmentsSection } from "@/features/orders/components/OrderAttachmentsSection";
import { OrderChangePanel } from "@/features/orders/components/OrderChangePanel";
import { OrderCommentsSection } from "@/features/orders/components/OrderCommentsSection";
import { OrderTypeBadge } from "@/features/orders/components/OrderTypeBadge";
import { ExternalCostsSection } from "@/features/orders/components/ExternalCostsSection";
import { OrderProjectFilesSection } from "@/features/orders/components/OrderProjectFilesSection";
import { OrderMilestonesSection } from "@/features/orders/components/OrderMilestonesSection";
import { OrderResultFilesSection } from "@/features/orders/components/OrderResultFilesSection";
import { ContractorInstructionsPanel } from "@/features/orders/components/ContractorInstructionsPanel";
import { OrderOwnersListSection } from "@/features/orders/components/OrderOwnersListSection";
// DLC_01 bridge: WPW handover (działka | WPW) — self-hides for non-WPW orders.
import { WpwHandoverPanel } from "@/features/power_projects/components/WpwHandoverPanel";
import { DesignStageOrderPanel } from "@/features/power_projects/components/DesignStageOrderPanel";
import { WodyOrderCrossingsPanel } from "@/features/power_projects/components/WodyOrderCrossingsPanel";
// DLC_01 bridge: MDCP photo points on a map — self-hides for non-MDCP orders. The surveyor
// clicks a point and uploads the photo, so we receive it pinned to that exact point.
import { MdcpContractorMap } from "@/features/power_projects/components/MdcpContractorMap";
import { usePockRequests, useSubmitPockRequest, useWithdrawPockRequest } from "../hooks/usePockRequests";
import { useMyLedProjectByOrder } from "@/features/contractor_led/hooks";
import { pockRequestsApi } from "../api/pockRequests";
import { useWeeklyReports, useSubmitWeeklyReport } from "../hooks/useWeeklyReports";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useConversationDeepLink } from "@/hooks/useConversationDeepLink";
import { acceptedQtyFromPct, completionPctFromQty, qtyInputValue } from "@/features/deliveries/utils/pockMath";
import { DocumentAttachmentsPanel } from "@/features/deliveries/components/DocumentAttachmentsPanel";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";

/** DRF error payloads come as a string, a list, or nested {field: [msgs]} — one line of text either way. */
function flattenApiErrors(errors: unknown): string {
  if (!errors) return "";
  if (typeof errors === "string") return errors;
  if (Array.isArray(errors)) return errors.map(flattenApiErrors).filter(Boolean).join(" ");
  if (typeof errors === "object") return Object.values(errors as Record<string, unknown>).map(flattenApiErrors).filter(Boolean).join(" ");
  return String(errors);
}

interface ContractorProtocolSummary {
  id: string;
  protocol_number: string;
  is_final: boolean;
  is_withdrawn: boolean;
  /** POCK bez kwot — przekazanie prac przez pracownika z dostępem „Realizacja”. */
  without_amounts?: boolean;
  // Rozliczony poza obiegiem — podwykonawca takiego nie dostaje (zakres API), podgląd GK go chowa.
  is_archived: boolean;
  accepted_by_contractor: boolean | null;
  created_at: string;
}


const statusColors: Record<OrderStatus, "gray" | "blue" | "green" | "red" | "yellow" | "purple" | "orange"> = {
  sent: "blue", accepted: "green", rejected: "red", in_progress: "orange",
  in_progress_partial: "yellow", submitted_for_acceptance: "purple",
  accepted_final: "green", closed: "gray", cancelled: "gray",
};

const ACTIVE_STATUSES: OrderStatus[] = [
  "sent", "accepted", "in_progress", "in_progress_partial", "submitted_for_acceptance",
];

function getNextReportDates(approvedAt: string, pythonDow: number): { nextDue: Date; windowOpens: Date } {
  // pythonDow: 0=Monday … 6=Sunday; JS getDay(): 0=Sun, 1=Mon … 6=Sat
  const jsDow = pythonDow === 6 ? 0 : pythonDow + 1;
  const start = new Date(approvedAt);
  start.setHours(0, 0, 0, 0);
  let daysUntil = (jsDow - start.getDay() + 7) % 7;
  if (daysUntil === 0) daysUntil = 7;
  const nextDue = new Date(start);
  nextDue.setDate(start.getDate() + daysUntil);
  const windowOpens = new Date(nextDue);
  windowOpens.setDate(nextDue.getDate() - 7);
  return { nextDue, windowOpens };
}

const pockRequestStatusColors: Record<PockRequestStatus, "yellow" | "green" | "red"> = {
  pending: "yellow",
  approved: "green",
  rejected: "red",
};

const fmtQty = (val: string | number) => String(Math.round(parseFloat(String(val)) || 0));

interface ContractorOrderDetailPageProps {
  /** Ustawiane w modalu „Widok podwykonawcy" (GK); poza podglądem id idzie z trasy. */
  previewOrderId?: string;
}

export function ContractorOrderDetailPage({ previewOrderId }: ContractorOrderDetailPageProps = {}) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = previewOrderId ?? routeId;
  const navigate = useNavigate();
  // Tryb „Widok podwykonawcy": użytkownik GK ogląda zamówienie oczami podwykonawcy —
  // dane idą z endpointu contractor-preview, a wszystkie akcje są wyłączone.
  // Tryb „Realizacja" (isExecution): pracownik GK z nadanym dostępem tego rodzaju —
  // żywe dane bez kwot, akcje wykonawcze (pliki, raporty, komentarze) działają,
  // wnioski o zmianę / POCK / koszty schowane, panele DLC_01 tylko do odczytu.
  const { isPreview, canSeeRates, ratesOverride, isExecution = false } = useContractorPreview();
  const { data: liveOrder, isLoading: liveLoading } = useOrder(id!, !isPreview);
  const { data: preview, isLoading: previewLoading } = useContractorOrderPreview(id!, isPreview, ratesOverride);
  const order = isPreview ? preview?.order : liveOrder;
  const isLoading = isPreview ? previewLoading : liveLoading;
  usePageTitle(!isPreview && order ? `Zamówienie ${order.order_number}` : undefined);
  // Zamówienie „Prowadzenie zlecenia" (apps.contractor_led): link do prowadzonego projektu.
  // W podglądzie też — endpoint odpowiada zamawiającemu (firma z zamówienia), a modal
  // blokuje samą nawigację, nie widok linku.
  // Konto firmy bez „Projektów" na karcie firmy nie ma dokąd iść — backend i tak da 404.
  const ledAccess = useAuthStore(
    (s) => s.user?.role !== "contractor" || s.user?.can_access_led_projects !== false,
  );
  const ledLink = useMyLedProjectByOrder(
    order?.id,
    ledAccess && !isExecution && order?.order_type === "project_lead",
  ).data;
  // Powiadomienie o komentarzu prowadzi do sekcji komentarzy, nie na górę zamówienia.
  const { ref: commentsRef } = useConversationDeepLink<HTMLDivElement>(!isLoading && !!order);
  const isActive = order ? ACTIVE_STATUSES.includes(order.status) : false;
  // Rate-blind podwykonawca dostaje 403 na penalty-calc — w podglądzie odwzorowujemy to,
  // nie wysyłając zapytania (użytkownik GK ma uprawnienia, więc dostałby dane).
  const { data: penaltyCalc } = usePenaltyCalc(id!, isActive && !!order?.deadline && !isExecution && (!isPreview || canSeeRates));
  const { data: appliedPenalties } = useAppliedPenalties(id!, isActive && !isExecution);

  // „Realizacja" nie składa ani nie ogląda wniosków o POCK — bez zapytania.
  const { data: rawPockRequests = [] } = usePockRequests(isExecution ? undefined : id);
  const submitPockRequest = useSubmitPockRequest(id!);
  const withdrawPockRequest = useWithdrawPockRequest();

  const { data: weeklyReports = [] } = useWeeklyReports(id, 30_000);
  const submitWeeklyReport = useSubmitWeeklyReport();

  const { data: rawProtocols = [] } = useQuery<ContractorProtocolSummary[]>({
    queryKey: ["contractor-protocols-for-order", id],
    queryFn: () =>
      apiClient
        .get<{ data: ContractorProtocolSummary[] }>("/protocols/", { params: { order: id } })
        .then((r) => r.data.data),
    enabled: !!id && !isExecution,
  });
  // Podgląd GK czyta te same endpointy własnym kontem, więc dostaje więcej niż podwykonawca:
  // zakres podwykonawcy wycina POCK-i ZARCHIWIZOWANE (rozliczone poza obiegiem) i wnioski
  // z takim POCK-iem, a bez wglądu w stawki maskuje kwoty. Tu odwzorowujemy oba zakresy
  // (reguły spisane w backend/apps/orders/tests/test_contractor_view_parity.py).
  const orderProtocols = useMemo(
    () => (isPreview ? rawProtocols.filter((p) => !p.is_archived) : rawProtocols),
    [isPreview, rawProtocols],
  );
  const pockRequests = useMemo(() => {
    if (!isPreview) return rawPockRequests;
    const visible = rawPockRequests.filter((r) => !r.protocol_archived);
    if (canSeeRates) return visible;
    return visible.map((r) => ({
      ...r,
      total_amount_net: null,
      items: r.items.map((it) => ({
        ...it,
        order_line_item_unit_rate: null,
        live_unit_rate: null,
        amount_net: null,
      })),
    }));
  }, [isPreview, canSeeRates, rawPockRequests]);
  const [uploadingReportId, setUploadingReportId] = useState<string | null>(null);
  const [reportFiles, setReportFiles] = useState<File[]>([]);
  const [reportUploadError, setReportUploadError] = useState("");
  const reportFileInputRef = useRef<HTMLInputElement>(null);

  const [showPockForm, setShowPockForm] = useState(false);
  const [pockNote, setPockNote] = useState("");
  const [pockSettlementPct, setPockSettlementPct] = useState("100");
  const [pockIsFinal, setPockIsFinal] = useState(false);
  const [pockQtys, setPockQtys] = useState<Record<string, string>>({});
  const [pockFiles, setPockFiles] = useState<Record<string, File[]>>({});
  // Files of the whole request (acceptance documentation, correspondence) — not of one line.
  const [pockDocFiles, setPockDocFiles] = useState<File[]>([]);
  const [pockSubmitError, setPockSubmitError] = useState("");
  const { user: currentUser } = useAuthStore();
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(new Set());

  if (isLoading) return <PageLoader />;
  if (!order) return <p className="text-gray-500 dark:text-gray-400">Nie znaleziono zamówienia.</p>;

  const total = order.line_items.reduce((sum, item) => sum + parseFloat(item.total_value), 0);
  const isOverdue = penaltyCalc && penaltyCalc.days_overdue > 0;

  const openPockForm = () => {
    const initialQtys: Record<string, string> = {};
    const initialFiles: Record<string, File[]> = {};
    order.line_items.forEach((li) => {
      initialQtys[li.id] = li.quantity;
      initialFiles[li.id] = [];
    });
    setPockQtys(initialQtys);
    setPockFiles(initialFiles);
    setPockDocFiles([]);
    setPockNote("");
    setPockSettlementPct("100");
    setPockIsFinal(false);
    setPockSubmitError("");
    setShowPockForm(true);
  };

  const pockFormTotal = order
    ? order.line_items.reduce((sum, li) => {
        const qty = parseFloat(pockQtys[li.id] ?? li.quantity) || 0;
        return sum + qty * parseFloat(li.unit_rate);
      }, 0)
    : 0;

  const requiredFileCount = (li: { id: string; attachment_required: boolean; attachment_count_per_unit: boolean; quantity: string }) => {
    if (!li.attachment_required) return 0;
    if (li.attachment_count_per_unit) {
      const pockQty = parseFloat(pockQtys[li.id] ?? li.quantity);
      return Math.ceil(pockQty || 1);
    }
    return 1;
  };

  const submitPockForm = async () => {
    setPockSubmitError("");
    const items = order.line_items.map((li) => {
      const qty = pockQtys[li.id] ?? "0";
      // The quantity is what the server stores and derives the percentage from;
      // the percentage rides along at full column precision for older backends —
      // rounding it here is what put stray groszy on POCKs.
      const completion_percentage = completionPctFromQty(qty, li.quantity);
      return { order_line_item: li.id, accepted_quantity: qtyInputValue(parseFloat(qty) || 0), completion_percentage };
    });
    try {
      const created = await submitPockRequest.mutateAsync({
        note: pockNote,
        proposed_settlement_pct: pockSettlementPct,
        proposed_is_final: pockIsFinal,
        items,
      });
      // Upload attachments for items that require them
      const uploadTasks = created.items
        .map((createdItem) => {
          const files = pockFiles[createdItem.order_line_item] ?? [];
          if (files.length > 0) {
            return pockRequestsApi.uploadItemAttachments(created.id, createdItem.id, files);
          }
          return null;
        })
        .filter(Boolean);
      if (pockDocFiles.length > 0) {
        uploadTasks.push(pockRequestsApi.uploadDocumentAttachments(created.id, pockDocFiles));
      }
      await Promise.all(uploadTasks);
      setShowPockForm(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: unknown } } };
      setPockSubmitError(flattenApiErrors(err?.response?.data?.errors) || "Błąd zapisu wniosku. Spróbuj ponownie.");
    }
  };

  const pockBlockedReason = order.pock_request_blocked_reason ?? null;
  // "Złóż nowy wniosek" once there is any history (typically a rejected one) —
  // the rejected request stays in the list as the archive entry.
  const hasPreviousRequests = pockRequests.length > 0;

  // Panele DLC_01 (WPW, etap projektowy, Wody) w trybie „Realizacja" zachowują się jak
  // w podglądzie GK: wszystko widać, żadna akcja nie żyje — ich zapisy są dla
  // podwykonawcy, a grant „Realizacja" ich nie obejmuje.
  const dlcReadOnly = (node: React.ReactNode) =>
    isExecution ? (
      <ContractorPreviewProvider value={{ isPreview: true, canSeeRates: false }}>{node}</ContractorPreviewProvider>
    ) : (
      node
    );

  const toggleExpand = (reqId: string) => {
    setExpandedRequests((prev) => {
      const next = new Set(prev);
      if (next.has(reqId)) next.delete(reqId);
      else next.add(reqId);
      return next;
    });
  };

  return (
    <div className="max-w-7xl lg:flex lg:gap-6 lg:items-start">
      {/* Główna treść zamówienia (lewa kolumna) */}
      <div className="max-w-4xl lg:flex-1 lg:min-w-0">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Badge color={order.priority === "high" ? "red" : "blue"}>{PRIORITY_LABELS[order.priority]}</Badge>
          <Badge color={statusColors[order.status]}>{ORDER_STATUS_LABELS[order.status]}</Badge>
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 break-words">{order.order_number}</h1>
            <OrderTypeBadge type={order.order_type} display={order.order_type_display} />
          </div>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
          {ledLink && (
            <Link
              to={`/contractor/projects/${ledLink.leadership_id}`}
              className="mt-2 inline-block border border-[var(--color-primary)] px-2 py-1 text-sm font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white"
            >
              Prowadzony projekt: {ledLink.name} →
            </Link>
          )}
        </div>
        {/* Przedsięwzięcie jest celowo ukryte przed podwykonawcą. */}
        {/* Data złożenia zamówienia (approved_at) — podwykonawca musi wiedzieć, kiedy zlecenie
            do niego trafiło; przy zamówieniach z terminem potwierdzenia pokazujemy też ten termin. */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Zamówienie złożone:{" "}
          <span className="font-medium text-gray-800 dark:text-gray-200">
            {new Date(order.approved_at).toLocaleString("pl", {
              day: "2-digit", month: "2-digit", year: "numeric",
              hour: "2-digit", minute: "2-digit",
            })}
          </span>
          {order.confirmation_deadline && (
            <span className="text-gray-500 dark:text-gray-400">
              {" · termin potwierdzenia: "}
              <span className="font-medium">
                {new Date(order.confirmation_deadline).toLocaleDateString("pl")}
              </span>
            </span>
          )}
        </p>
        {/* Termin przyjęcia (backend: apps.orders.acceptance_deadline). Po nim zamówienie nie
            znika — nadal można je przyjąć, ale zamawiający może je w każdej chwili anulować. */}
        {order.status === "sent" && order.confirmation_deadline && (order.acceptance_deadline_tracked || order.acceptance_overdue_since) && (
          <p
            data-testid="contractor-acceptance-deadline-warning"
            className="mt-1 flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400"
          >
            <Clock size={14} className="shrink-0" />
            {order.acceptance_overdue_since
              ? `Termin przyjęcia minął ${formatDate(order.confirmation_deadline)} — zamówienie nadal możesz przyjąć, ale zamawiający może je anulować.`
              : `Prosimy o przyjęcie zamówienia do ${formatDate(order.confirmation_deadline)} — po tym terminie zamawiający może je anulować.`}
          </p>
        )}
        {order.deadline && (
          <p className={`text-base font-semibold mt-2 ${isOverdue ? "text-red-600 dark:text-red-400" : "text-gray-700 dark:text-gray-300"}`}>
            Termin realizacji: {formatDate(order.deadline)}
          </p>
        )}
      </div>

      {/* Obie strony zamówienia obok siebie — kto je złożył po stronie GK ELEKTIM i kto
          je przyjął po naszej. Kontakty osób pochodzą na żywo z ich profili. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6 items-start">
        {/* Zamawiający — osoba odpowiedzialna po stronie GK ELEKTIM. Podwykonawca musi
            wiedzieć, kto złożył zamówienie i jak się z nim skontaktować. Pod nim —
            pracownicy GK z dostępem „Zarządzanie”/„Realizacja” (kontakty, może być kilku). */}
        <div className="flex flex-col gap-3">
        <div className="bg-white dark:bg-gray-800 p-4 border-l-4 border-[var(--color-primary)]">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Zamawiający (osoba odpowiedzialna)</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{order.ordered_by_name ?? "—"}</p>
          <div className="mt-2 flex flex-col gap-1 text-sm">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Phone size={13} className="shrink-0" />
              {order.ordered_by_phone ? (
                <a href={`tel:${order.ordered_by_phone}`} className="hover:underline break-all">{order.ordered_by_phone}</a>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">brak numeru</span>
              )}
            </span>
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Mail size={13} className="shrink-0" />
              {order.ordered_by_email ? (
                <a href={`mailto:${order.ordered_by_email}`} className="hover:underline break-all">{order.ordered_by_email}</a>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">brak adresu</span>
              )}
            </span>
          </div>
        </div>
        <OrderGrantedStaffCard staff={order.granted_staff} />
        </div>

        {/* Nasza firma i osoba, która przyjęła to zlecenie — ten sam komplet danych, który
            widzi koordynator, żeby obie strony rozmawiały o tej samej osobie. */}
        <div className="bg-white dark:bg-gray-800 p-4 border-l-4 border-amber-400">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Wykonawca (przyjmujący zlecenie)</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words flex items-center gap-1.5">
            <Building2 size={13} className="shrink-0 text-gray-400 dark:text-gray-500" />
            {order.contractor_name}
          </p>
          <div className="mt-2 flex flex-col gap-1 text-sm">
            {order.contractor_contact_person && (
              <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <UserIcon size={13} className="shrink-0" />
                <span className="break-words">{order.contractor_contact_person}</span>
              </span>
            )}
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Phone size={13} className="shrink-0" />
              {order.contractor_contact_phone ? (
                <a href={`tel:${order.contractor_contact_phone}`} className="hover:underline break-all">{order.contractor_contact_phone}</a>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">brak numeru</span>
              )}
            </span>
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Mail size={13} className="shrink-0" />
              {order.contractor_contact_email ? (
                <a href={`mailto:${order.contractor_contact_email}`} className="hover:underline break-all">{order.contractor_contact_email}</a>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">brak adresu</span>
              )}
            </span>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
              Zlecenie przyjął(-ęła)
              {order.accepted_at ? ` — ${new Date(order.accepted_at).toLocaleDateString("pl")}` : ""}
            </p>
            {order.accepted_by_name ? (
              <>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{order.accepted_by_name}</p>
                <div className="mt-2 flex flex-col gap-1 text-sm">
                  <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <Phone size={13} className="shrink-0" />
                    {order.accepted_by_phone ? (
                      <a href={`tel:${order.accepted_by_phone}`} className="hover:underline break-all">{order.accepted_by_phone}</a>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">brak numeru</span>
                    )}
                  </span>
                  <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <Mail size={13} className="shrink-0" />
                    {order.accepted_by_email ? (
                      <a href={`mailto:${order.accepted_by_email}`} className="hover:underline break-all">{order.accepted_by_email}</a>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">brak adresu</span>
                    )}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {order.status === "sent"
                  ? "Zlecenie czeka na potwierdzenie — osoba, która je przyjmie, pojawi się tutaj"
                  : "Brak — zlecenie przyjęte bez potwierdzenia osoby"}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Overdue penalty warning */}
      {isOverdue && penaltyCalc && (
        <div className="mb-6 rounded-xl border border-red-300 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-red-700">
                Zamówienie opóźnione o {penaltyCalc.days_overdue} {penaltyCalc.days_overdue === 1 ? "dzień" : "dni"}
              </p>
              <p className="text-sm text-red-600 mt-0.5">
                Termin realizacji: <span className="font-medium">{formatDate(penaltyCalc.deadline)}</span>
              </p>
              {penaltyCalc.terms.length > 0 && (
                <div className="mt-2 text-sm text-red-700">
                  <p className="font-medium mb-1">Możliwa kara umowna:</p>
                  {penaltyCalc.terms.map((term) => (
                    <p key={term.term_id} className="flex justify-between gap-4">
                      <span>{term.description}</span>
                      <span className="font-semibold whitespace-nowrap">
                        {formatAmount(term.calculated_amount)} PLN
                      </span>
                    </p>
                  ))}
                  {penaltyCalc.terms.length > 1 && (
                    <p className="flex justify-between gap-4 font-bold border-t border-red-200 pt-1 mt-1">
                      <span>Łącznie możliwa kara</span>
                      <span>{formatAmount(penaltyCalc.total_calculated_penalty)} PLN</span>
                    </p>
                  )}
                </div>
              )}
              {penaltyCalc.terms.length === 0 && penaltyCalc.agreement_id && (
                <p className="text-sm text-red-600 mt-1">Brak zdefiniowanych kar w umowie ramowej.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Applied penalty records */}
      {appliedPenalties && (
        parseFloat(appliedPenalties.accumulated_daily_amount) > 0 ||
        appliedPenalties.daily_penalty_forgiven ||
        appliedPenalties.onetime_records.length > 0
      ) && (
        <div className="mb-6 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Naliczone kary umowne</p>
          </div>
          <div className="px-4 py-4 flex flex-col gap-3">
            {/* Daily penalty */}
            {(parseFloat(appliedPenalties.accumulated_daily_amount) > 0 || appliedPenalties.daily_penalty_forgiven) && (
              <div className={`flex items-start gap-3 px-3 py-2.5 ${appliedPenalties.daily_penalty_forgiven ? "bg-gray-50 dark:bg-gray-800/50 " : "bg-red-50 border border-red-200"}`}>
                {appliedPenalties.daily_penalty_forgiven
                  ? <XCircle size={16} className="text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                  : <Clock size={16} className="text-red-500 flex-shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${appliedPenalties.daily_penalty_forgiven ? "text-gray-600 dark:text-gray-400" : "text-red-800"}`}>
                    {appliedPenalties.daily_penalty_forgiven
                      ? "Kara za opóźnienie — darowana"
                      : `Kara za opóźnienie: ${formatAmount(appliedPenalties.accumulated_daily_amount)} PLN`}
                  </p>
                  {appliedPenalties.daily_penalty_forgiven && appliedPenalties.daily_penalty_forgiven_note && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{appliedPenalties.daily_penalty_forgiven_note}</p>
                  )}
                </div>
                {parseFloat(appliedPenalties.settled_amount) > 0 && (
                  <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full flex-shrink-0">
                    <CheckCircle2 size={11} /> {formatAmount(appliedPenalties.settled_amount)} PLN uregulowane
                  </span>
                )}
              </div>
            )}

            {/* One-time records */}
            {appliedPenalties.onetime_records.map((rec) => (
              <div key={rec.id} className={`flex items-start gap-3 rounded-lg px-3 py-2.5 border ${rec.is_forgiven ? "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700" : rec.is_settled ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
                {rec.is_forgiven
                  ? <XCircle size={16} className="text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                  : rec.is_settled
                  ? <CheckCircle2 size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  : <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{rec.description}</p>
                  <p className={`text-xs mt-0.5 ${rec.is_forgiven ? "text-gray-500 dark:text-gray-400" : rec.is_settled ? "text-green-700" : "text-amber-700"}`}>
                    {formatAmount(rec.amount)} PLN
                    {rec.is_forgiven && " — darowana"}
                    {rec.is_settled && rec.settlement_note && ` — ${rec.settlement_note}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {order.description && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Opis zamówienia</p>
          {order.description.trimStart().startsWith("<") ? (
            <div
              className="rich-text-content text-sm text-gray-600 dark:text-gray-400"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(order.description, {
                  ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"],
                  ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
                }),
              }}
            />
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{order.description}</p>
          )}
        </div>
      )}

      {/* Attachments carried over from the requisition (multifile). Ta sama sekcja co w
          widoku GK — podwykonawca dostaje dzięki temu pobranie wszystkich załączników
          jednym ZIP-em (endpoint pilnuje widoczności zamówienia). `canManage={false}`:
          bez dodawania i usuwania plików; sekcja sama znika, gdy nie ma załączników. */}
      <OrderAttachmentsSection orderId={order.id} files={order.files_detail ?? []} canManage={false} />

      {/* Pliki z projektu ZE udostępnione przy zleceniu — żywa projekcja zakładki „Pliki"
          projektu; sekcja sama znika, gdy brak udostępnionych plików. */}
      <OrderProjectFilesSection orderId={order.id} enabled={!!order.has_project_files_share} />

      {/* Kamienie milowe (terminy pośrednie) — wykonujący (podwykonawca albo pracownik GK
          z dostępem „Realizacja", isExecution) oznacza tu wykonanie kamienia (`canComplete`:
          ten sam endpoint i modal co w „Moich terminach"); bez edycji dat, bez cofania i
          bez wewnętrznej historii zmian (`events`), która jest audytem GK. Przy wykonanym
          kamieniu widać datę i kto odhaczył. Podgląd GK (isPreview) bez akcji. Filtr
          statusu (wszystkie / w trakcie / wykonane) tylko tu. Sekcja sama znika, gdy
          zamówienie nie ma kamieni. */}
      <OrderMilestonesSection
        orderId={order.id}
        milestones={order.milestones ?? []}
        canManage={false}
        canComplete={!isPreview}
        filterable
      />

      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 border-l-4 border-[var(--color-primary)] pl-3">Pozycje realizacyjne</h2>
      <div className="bg-white dark:bg-gray-800 overflow-x-auto">
        <table className="w-full text-xs min-w-[500px]">
          <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Jm.</th>
              <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość</th>
              <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka</th>
              <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Wartość</th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zał. wymagany</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {order.line_items.map((item) => (
              <tr key={item.id} className={`border-l-2 transition-colors ${item.is_custom ? "border-amber-400 bg-amber-50/30 dark:bg-amber-900/10" : "border-transparent hover:border-[var(--color-primary)]"}`}>
                <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                  {item.is_custom && <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 mr-1">[N]</span>}
                  {item.line_item_type_name}
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{item.line_item_type_unit}</td>
                <td className="px-4 py-3 text-right">{fmtQty(item.quantity)}</td>
                <td className="px-4 py-3 text-right">{item.unit_rate != null ? `${formatAmount(item.unit_rate)} PLN` : ""}</td>
                <td className="px-4 py-3 text-right font-medium">{item.total_value != null ? `${formatAmount(item.total_value)} PLN` : ""}</td>
                <td className="px-4 py-3">
                  <Badge color={item.attachment_required ? "blue" : "gray"}>
                    {item.attachment_required ? `Tak${item.required_file_extension ? ` (.${item.required_file_extension})` : ""}` : "Nie"}
                  </Badge>
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50 dark:bg-gray-800/50 font-semibold">
              <td colSpan={4} className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">Łącznie:</td>
              <td colSpan={2} className="px-4 py-3 text-right">{order.amounts_hidden ? "—" : `${formatAmount(total)} PLN`}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Order change requests — right under the line items they change; nie dla
          „Realizacji" (grant bez wniosków o zmianę). */}
      {!isExecution && (
        <div className="mt-8 bg-white dark:bg-gray-800 p-5">
          <OrderChangePanel orderId={order.id} orderStatus={order.status} lineItems={order.line_items} />
        </div>
      )}

      {/* WPW handover (działka | WPW) — negotiator edits status/comments/files here */}
      <div className="mt-8">
        {dlcReadOnly(
          <WpwHandoverPanel
            orderId={order.id}
            ownersList={
              <OrderOwnersListSection
                orderId={order.id}
                orderType={order.order_type}
                file={order.mml_file_detail ?? null}
                generatedAt={order.mml_file_generated_at ?? null}
                readOnly={isPreview || isExecution}
              />
            }
          />,
        )}
      </div>

      {/* Design stage (Projekt techniczny / Kosztorys) — subcontractor's path for this order */}
      <div className="mt-8">
        {dlcReadOnly(<DesignStageOrderPanel orderId={order.id} orderType={order.order_type} />)}
      </div>

      {/* „Realizacja": przekazanie wykonanych prac przez POCK bez kwot (pozycje, procent,
          załączniki) — dla każdego typu zamówienia; zwykłe wnioski o POCK są dla niej schowane. */}
      {isExecution && <ExecutorPockSection order={order} />}

      {/* POCK requests — nie dla „Realizacji" (grant bez składania POCK). */}
      {!isExecution && (
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-l-4 border-[var(--color-primary)] pl-3">Wnioski o POCK</h2>
          {!showPockForm && (
            <Button
              size="sm"
              disabled={isPreview || !!pockBlockedReason}
              onClick={openPockForm}
              title={pockBlockedReason ?? undefined}
            >
              <Plus size={14} /> {hasPreviousRequests ? "Złóż nowy wniosek o odbiór" : "Złóż wniosek"}
            </Button>
          )}
        </div>
        {pockBlockedReason && !showPockForm && (
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-3" role="note">
            {pockBlockedReason}
          </p>
        )}

        {/* Submission form */}
        {showPockForm && (
          <div className="bg-white dark:bg-gray-800 p-5 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Nowy wniosek o POCK</h3>
            {!order.allow_partial_pock && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-300">
                Umowa wymaga pełnego odbioru — wniosek obejmuje 100% wszystkich pozycji, czyli ilości z zamówienia.
                Jeśli wykonano mniej niż zamówiono, najpierw złóż wniosek o zmianę ilości (sekcja „Wnioski o zmianę
                zamówienia” powyżej), a po jego zatwierdzeniu nowy wniosek o odbiór.
              </div>
            )}
            <div className="bg-white dark:bg-gray-800 overflow-x-auto mb-4">
              <table className="w-full text-xs min-w-[400px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Jm.</th>
                    <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówiono</th>
                    <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Odebrano</th>
                    <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Wartość netto</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Załącznik</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {order.line_items.map((li) => {
                    const itemAmt = (parseFloat(pockQtys[li.id] ?? li.quantity) || 0) * parseFloat(li.unit_rate);
                    return (
                    <tr key={li.id}>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{li.line_item_type_name}</td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{displayUnit(li.line_item_type_unit)}</td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{fmtQty(li.quantity)}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          max={parseFloat(li.quantity)}
                          step="any"
                          value={pockQtys[li.id] ?? fmtQty(li.quantity)}
                          readOnly={!order.allow_partial_pock}
                          onChange={(e) => {
                            if (order.allow_partial_pock) setPockQtys((prev) => ({ ...prev, [li.id]: e.target.value }));
                          }}
                          className={`w-24 text-right rounded border px-2 py-1 text-sm ${!order.allow_partial_pock ? "bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-500 cursor-not-allowed" : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {formatAmount(itemAmt)} PLN
                      </td>
                      <td className="px-3 py-2">
                        {(() => {
                          const needed = requiredFileCount(li);
                          const files = pockFiles[li.id] ?? [];
                          const ok = needed > 0 && files.length >= needed;
                          return (
                            <div className="flex flex-col gap-1">
                              <label className="cursor-pointer inline-flex items-center gap-1.5">
                                <input
                                  type="file"
                                  accept={li.required_file_extension ? `.${li.required_file_extension}` : undefined}
                                  multiple
                                  className="sr-only"
                                  onChange={(e) => {
                                    const newFiles = Array.from(e.target.files ?? []);
                                    setPockFiles((prev) => ({
                                      ...prev,
                                      [li.id]: [...(prev[li.id] ?? []), ...newFiles],
                                    }));
                                    e.target.value = "";
                                  }}
                                />
                                <span className={`text-xs px-2 py-1 rounded border ${
                                  needed === 0
                                    ? "border-gray-300 text-gray-500 bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:bg-gray-800"
                                    : ok
                                    ? "border-green-400 text-green-700 bg-green-50"
                                    : "border-amber-400 text-amber-700 bg-amber-50"
                                }`}>
                                  {needed === 0
                                    ? files.length > 0
                                      ? `${files.length} dodano`
                                      : "Dodaj (opcjonalnie)"
                                    : ok
                                    ? `${files.length} / ${needed} ✓`
                                    : `Dodaj (${files.length}/${needed}${li.required_file_extension ? ` .${li.required_file_extension}` : ""})`
                                  }
                                </span>
                              </label>
                              {files.length > 0 && (
                                <ul className="flex flex-col gap-0.5">
                                  {files.map((f, i) => (
                                    <li key={i} className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-400">
                                      <button
                                        type="button"
                                        onClick={() => openPendingFile(f)}
                                        className="truncate max-w-[120px] hover:underline hover:text-[var(--color-primary)]"
                                        title={`${f.name} — kliknij, aby podejrzeć`}
                                      >
                                        {f.name}
                                      </button>
                                      <button
                                        type="button"
                                        className="text-red-400 hover:text-red-600 flex-shrink-0"
                                        onClick={() => setPockFiles((prev) => ({
                                          ...prev,
                                          [li.id]: prev[li.id]?.filter((_, j) => j !== i) ?? [],
                                        }))}
                                      >×</button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 dark:bg-gray-800/50 font-semibold">
                  <tr>
                    <td colSpan={4} className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 text-xs">Łącznie:</td>
                    <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatAmount(pockFormTotal)} PLN</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="mb-4 flex items-end gap-6">
              {order.allow_partial_pock && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Proponowany % rozliczenia pozycji ujętych w POCK
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={pockSettlementPct}
                    onChange={(e) => setPockSettlementPct(e.target.value)}
                    className="w-24 text-right rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>
              )}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Uwagi (opcjonalne)</label>
                <textarea
                  value={pockNote}
                  onChange={(e) => setPockNote(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                  placeholder="Dodatkowe informacje dla koordynatora..."
                />
              </div>
            </div>
            <label className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 cursor-pointer">
              <input
                type="checkbox"
                checked={pockIsFinal}
                onChange={(e) => setPockIsFinal(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">POCK końcowy</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                — ostatni odbiór w tym zamówieniu; GK ELEKTIM potwierdza to przy zatwierdzeniu wniosku
              </span>
            </label>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Pliki do całego wniosku (opcjonalnie)
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                Dokumenty dotyczące całego odbioru (np. dokumentacja odbiorowa, korespondencja) — nie trzeba ich
                przypinać do żadnej pozycji.
              </p>
              <label className="cursor-pointer inline-flex items-center gap-1.5">
                <input
                  type="file"
                  multiple
                  className="sr-only"
                  data-testid="pock-doc-files"
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files ?? []);
                    setPockDocFiles((prev) => [...prev, ...newFiles]);
                    e.target.value = "";
                  }}
                />
                <span className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:bg-gray-800">
                  {pockDocFiles.length > 0 ? `Dodano: ${pockDocFiles.length} — dodaj kolejne` : "Dodaj pliki"}
                </span>
              </label>
              {pockDocFiles.length > 0 && (
                <ul className="mt-1.5 flex flex-col gap-0.5">
                  {pockDocFiles.map((f, i) => (
                    <li key={i} className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                      <button
                        type="button"
                        onClick={() => openPendingFile(f)}
                        className="truncate max-w-[260px] hover:underline hover:text-[var(--color-primary)]"
                        title={`${f.name} — kliknij, aby podejrzeć`}
                      >
                        {f.name}
                      </button>
                      <button
                        type="button"
                        className="text-red-400 hover:text-red-600 flex-shrink-0"
                        aria-label={`Usuń ${f.name}`}
                        onClick={() => setPockDocFiles((prev) => prev.filter((_, j) => j !== i))}
                      >×</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {pockSubmitError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-3">{pockSubmitError}</p>
            )}
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={submitPockForm} disabled={submitPockRequest.isPending}>
                <Send size={14} /> {submitPockRequest.isPending ? "Wysyłanie…" : "Wyślij wniosek"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowPockForm(false)}>
                Anuluj
              </Button>
            </div>
          </div>
        )}

        {/* List of existing requests */}
        {pockRequests.length === 0 && !showPockForm ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak wniosków o POCK dla tego zamówienia.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {pockRequests.map((req) => {
              const isExpanded = expandedRequests.has(req.id);
              return (
                <div key={req.id} className={`bg-white dark:bg-gray-800 rounded-xl border overflow-hidden ${req.is_withdrawn ? "border-gray-200 dark:border-gray-700 opacity-60" : "border-gray-200 dark:border-gray-700"}`}>
                  <div className="flex items-center justify-between pr-3">
                    <button
                      onClick={() => toggleExpand(req.id)}
                      className="flex-1 flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-colors text-left"
                    >
                      <div className={`flex items-center gap-3 flex-wrap ${req.is_withdrawn || req.protocol_withdrawn ? "line-through opacity-60" : ""}`}>
                        {req.is_withdrawn ? (
                          <Badge color="gray">Wycofany</Badge>
                        ) : req.protocol_withdrawn ? (
                          <Badge color="gray">POCK wycofany przez GK ELEKTIM</Badge>
                        ) : (
                          <Badge color={pockRequestStatusColors[req.status]}>
                            {POCK_REQUEST_STATUS_LABELS[req.status]}
                          </Badge>
                        )}
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {new Date(req.created_at).toLocaleDateString("pl")}
                        </span>
                        {req.proposed_is_final && <Badge color="purple">Końcowy</Badge>}
                        {req.is_stale && req.status === "pending" && !req.is_withdrawn && (
                          <Badge color="red">Nieaktualny — zamówienie zmieniło się po złożeniu</Badge>
                        )}
                        {!req.is_withdrawn && !req.protocol_withdrawn && (
                          <>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {req.approved_settlement_pct != null
                                ? <>Rozliczenie: <strong>{Math.round(parseFloat(req.approved_settlement_pct))}%</strong></>
                                : <>Proponowane: <strong>{Math.round(parseFloat(req.proposed_settlement_pct))}%</strong></>
                              }
                            </span>
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                              {formatAmount(req.total_amount_net)} PLN
                            </span>
                          </>
                        )}
                        {req.note && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 w-full whitespace-normal break-words">{req.note}</span>
                        )}
                      </div>
                      {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                    </button>
                    {req.status === "pending" && !req.is_withdrawn && (
                      <button
                        className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 px-2 py-1 rounded transition-colors whitespace-nowrap disabled:opacity-50 disabled:hover:text-gray-400"
                        onClick={() => withdrawPockRequest.mutate(req.id)}
                        disabled={withdrawPockRequest.isPending || isPreview}
                        title={isPreview ? "Podgląd — akcje niedostępne" : "Wycofaj wniosek"}
                      >
                        Wycofaj
                      </button>
                    )}
                    {req.status === "rejected" && !showPockForm && !pockBlockedReason && (
                      <button
                        className="text-xs text-[var(--color-primary)] hover:underline px-2 py-1 rounded whitespace-nowrap disabled:opacity-50"
                        onClick={openPockForm}
                        disabled={isPreview}
                        title="Odrzucony wniosek zostaje w historii — złóż nowy"
                      >
                        Złóż nowy wniosek o odbiór
                      </button>
                    )}
                  </div>
                  {isExpanded && (
                    <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3">
                      {req.is_stale && req.status === "pending" && !req.is_withdrawn && (
                        <p className="text-sm mb-3 text-red-600 dark:text-red-400">{req.stale_reason}</p>
                      )}
                      {req.review_comment && (
                        <p className="text-sm mb-3">
                          <span className="font-medium text-gray-700 dark:text-gray-300">Komentarz GK: </span>
                          <span className={req.status === "rejected" ? "text-red-600 dark:text-red-400" : "text-gray-600 dark:text-gray-400"}>
                            {req.review_comment}
                          </span>
                        </p>
                      )}
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-800/50">
                          <tr>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
                            <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówiono</th>
                            <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Do odbioru</th>
                            <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">% rozl.</th>
                            <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Wartość netto</th>
                            <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Załączniki</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {req.items.map((item) => {
                            // order_line_item_quantity is the snapshot as submitted; the
                            // typed quantity is stored, the percentage is only a fallback.
                            const qty = item.accepted_quantity != null
                              ? parseFloat(item.accepted_quantity)
                              : acceptedQtyFromPct(item.completion_percentage, item.order_line_item_quantity);
                            const settlementPct = Math.round(parseFloat(req.approved_settlement_pct ?? req.proposed_settlement_pct));
                            return (
                              <tr key={item.id}>
                                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{item.order_line_item_name}</td>
                                <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">
                                  {fmtQty(item.order_line_item_quantity)} {displayUnit(item.order_line_item_unit)}
                                </td>
                                <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                                  {fmtQty(qty)} {displayUnit(item.order_line_item_unit)}
                                </td>
                                <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{settlementPct}%</td>
                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                  {item.amount_net ? <>{formatAmount(item.amount_net)} PLN</> : "—"}
                                </td>
                                <td className="px-3 py-2">
                                  {item.attachments.length === 0 ? (
                                    <span className="text-xs text-gray-400">—</span>
                                  ) : (
                                    <div className="flex flex-col gap-0.5">
                                      {item.attachments.map((att) => (
                                        <a
                                          key={att.id}
                                          href={att.managed_file_detail?.url ?? "#"}
                                          download={att.original_name}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-xs text-[var(--color-primary)] hover:underline dark:text-[var(--color-primary)] truncate max-w-[160px]"
                                          title={att.original_name}
                                        >
                                          {att.original_name}
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-gray-50 dark:bg-gray-800/50 font-semibold">
                          <tr>
                            <td colSpan={4} className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 text-xs">Łącznie:</td>
                            <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                              {formatAmount(req.total_amount_net)} PLN
                            </td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                      <DocumentAttachmentsPanel
                        compact
                        title="Pliki całego wniosku"
                        attachments={req.document_attachments ?? []}
                        endpoint={`/protocols/pock-requests/${req.id}/attachments/`}
                        canUpload={
                          !isPreview &&
                          req.status === "pending" &&
                          !req.is_withdrawn &&
                          req.requested_by === currentUser?.id
                        }
                        invalidateKeys={[["pock-requests"]]}
                        readOnly={isPreview}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Protocols POCK section */}
      {!isExecution && orderProtocols.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 border-l-4 border-[var(--color-primary)] pl-3">
            Protokoły POCK
          </h2>
          <div className="bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[480px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nr protokołu</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Typ</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {orderProtocols.map((p) => {
                  const awaitingResponse = !p.is_withdrawn && p.accepted_by_contractor === null;
                  return (
                    <tr
                      key={p.id}
                      className={
                        p.is_withdrawn
                          ? "opacity-50"
                          : awaitingResponse
                          ? "bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20"
                          : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      }
                    >
                      <td className="px-4 py-3 font-medium">
                        <Link
                          to={`/contractor/protocols/${p.id}`}
                          className={`hover:underline ${p.is_withdrawn ? "line-through text-gray-400 dark:text-gray-500" : "text-[var(--color-primary)]"}`}
                        >
                          {p.protocol_number}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        {p.is_withdrawn ? (
                          <Badge color="gray">Wycofany</Badge>
                        ) : (
                          <Badge color={p.is_final ? "purple" : "blue"}>
                            {p.is_final ? "Końcowy" : "Częściowy"}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {p.is_withdrawn ? (
                          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                        ) : p.accepted_by_contractor === null ? (
                          <Badge color="yellow">Oczekuje na odpowiedź</Badge>
                        ) : p.accepted_by_contractor ? (
                          <Badge color="green">Zaakceptowany</Badge>
                        ) : (
                          <Badge color="red">Odrzucony</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {new Date(p.created_at).toLocaleDateString("pl")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Weekly reports */}
      {order.effective_requires_weekly_reports && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2 border-l-4 border-[var(--color-primary)] pl-3">
            <FileText size={18} />
            Cotygodniowe raporty postępu
          </h2>
          {/* Schedule info */}
          {(() => {
            const DOW_PL = ["poniedziałek", "wtorek", "środa", "czwartek", "piątek", "sobota", "niedziela"];
            const dayLabel = order.effective_report_day_of_week != null ? DOW_PL[order.effective_report_day_of_week] : null;
            const timeLabel = order.effective_report_due_time ? order.effective_report_due_time.slice(0, 5) : null;
            return (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Wymagane cotygodniowo
                {dayLabel && <> · termin: co <span className="font-medium">{dayLabel}</span></>}
                {timeLabel && <> do godz. <span className="font-medium">{timeLabel}</span></>}
                {" · okno składania otwiera się 7 dni przed terminem"}
              </p>
            );
          })()}
          {weeklyReports.length === 0 ? (
            (() => {
              const dow = order.effective_report_day_of_week;
              if (dow == null || !order.approved_at) {
                return <p className="text-sm text-gray-400 dark:text-gray-500">Brak raportów dla tego zamówienia.</p>;
              }
              const { nextDue, windowOpens } = getNextReportDates(order.approved_at, dow);
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const windowAlreadyOpen = windowOpens <= today;
              const daysUntilWindow = Math.ceil((windowOpens.getTime() - today.getTime()) / 86400000);
              const fmtPl = (d: Date) => `${d.toLocaleDateString("pl", { weekday: "long" })}, ${formatDate(d)}`;
              return (
                <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-sm space-y-1">
                  <p className="font-medium text-blue-800 dark:text-blue-300">Raport nie został jeszcze wygenerowany.</p>
                  <p className="text-blue-700 dark:text-blue-400">
                    Pierwszy termin złożenia: <span className="font-medium">{fmtPl(nextDue)}</span>
                  </p>
                  {windowAlreadyOpen ? (
                    <p className="text-blue-700 dark:text-blue-400">
                      Okno składania jest już otwarte — raport pojawi się po odświeżeniu strony.
                    </p>
                  ) : (
                    <p className="text-blue-700 dark:text-blue-400">
                      Okno składania otworzy się: <span className="font-medium">{fmtPl(windowOpens)}</span>
                      {" "}<span className="text-blue-500 dark:text-blue-500">(za {daysUntilWindow} {daysUntilWindow === 1 ? "dzień" : "dni"})</span>
                    </p>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="flex flex-col gap-2">
              {weeklyReports.map((report) => {
                const statusColor: Record<WeeklyReportStatus, "yellow" | "green" | "red" | "blue" | "gray"> = {
                  pending: "yellow", submitted: "blue", late: "red", accepted: "green", rejected: "red",
                };
                const isUploadOpen = uploadingReportId === report.id;
                const todayStr = new Date().toISOString().slice(0, 10);
                const canSubmit = (report.status === "pending" || report.status === "late" || report.status === "rejected")
                  && todayStr >= report.period_start;
                return (
                  <div
                    key={report.id}
                    role="button"
                    tabIndex={0}
                    className="bg-white dark:bg-gray-800 p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors rounded-xl border border-gray-200 dark:border-gray-700 hover:border-[var(--color-primary)]"
                    onClick={(e) => {
                      if (isPreview) return; // podgląd GK — bez nawigacji poza modal
                      // Don't navigate when interacting with inner buttons/inputs/links
                      const target = e.target as HTMLElement;
                      if (target.closest("button, a, input, textarea")) return;
                      navigate(`/orders/${id}/weekly-reports/${report.id}`);
                    }}
                    onKeyDown={(e) => {
                      if (isPreview) return;
                      if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                        e.preventDefault();
                        navigate(`/orders/${id}/weekly-reports/${report.id}`);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge color={statusColor[report.status]}>{WEEKLY_REPORT_STATUS_LABELS[report.status]}</Badge>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Termin: {new Date(report.due_date).toLocaleDateString("pl")}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Okres: {new Date(report.period_start).toLocaleDateString("pl")} – {new Date(report.period_end).toLocaleDateString("pl")}
                        </span>
                      </div>
                      {canSubmit && !isUploadOpen && (
                        <Button size="sm" variant="outline" disabled={isPreview} onClick={() => { setUploadingReportId(report.id); setReportFiles([]); setReportUploadError(""); }}>
                          <Upload size={13} /> Złóż raport
                        </Button>
                      )}
                    </div>

                    {report.director_comment && (
                      <p className="text-xs text-red-600 dark:text-red-400 mb-2">Komentarz dyrektora: {report.director_comment}</p>
                    )}

                    {/* Uploaded files */}
                    {report.files.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {report.files.map((f) => (
                          <a
                            key={f.id}
                            href={f.file_url ?? f.file}
                            download={f.original_name}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-[var(--color-primary)] hover:underline dark:text-[var(--color-primary)] truncate max-w-[200px]"
                            title={f.original_name}
                          >
                            {f.original_name}
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Upload form */}
                    {isUploadOpen && (
                      <div className="mt-2 border-t border-gray-100 dark:border-gray-700 pt-3">
                        <input
                          ref={reportFileInputRef}
                          type="file"
                          multiple
                          className="sr-only"
                          onChange={(e) => setReportFiles(Array.from(e.target.files ?? []))}
                        />
                        <div className="flex items-center gap-2 mb-2">
                          <Button size="sm" variant="outline" onClick={() => reportFileInputRef.current?.click()}>
                            Wybierz pliki
                          </Button>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {reportFiles.length > 0 ? `${reportFiles.length} plik(ów) wybranych` : "Brak plików"}
                          </span>
                        </div>
                        {reportFiles.length > 0 && (
                          <ul className="text-xs text-gray-600 dark:text-gray-400 mb-2 list-disc pl-4">
                            {reportFiles.map((f, i) => (
                              <li key={i}>
                                <button
                                  type="button"
                                  onClick={() => openPendingFile(f)}
                                  className="hover:underline hover:text-[var(--color-primary)]"
                                  title={`${f.name} — kliknij, aby podejrzeć`}
                                >
                                  {f.name}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {reportUploadError && (
                          <p className="text-xs text-red-600 dark:text-red-400 mb-2">{reportUploadError}</p>
                        )}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            disabled={reportFiles.length === 0 || submitWeeklyReport.isPending}
                            onClick={async () => {
                              setReportUploadError("");
                              try {
                                await submitWeeklyReport.mutateAsync({ id: report.id, files: reportFiles });
                                setUploadingReportId(null);
                              } catch {
                                setReportUploadError("Nie udało się złożyć raportu. Spróbuj ponownie.");
                              }
                            }}
                          >
                            <Send size={13} /> Wyślij
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setUploadingReportId(null)}>Anuluj</Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Wody Polskie — zamrożony wykaz przekroczeń i zbliżeń (zakres zamówienia). */}
      {order.order_type === "wody_polskie" && (
        <div className="mt-8">
          {dlcReadOnly(<WodyOrderCrossingsPanel orderId={order.id} />)}
        </div>
      )}

      {/* MDCP — mapa punktów zdjęciowych (geodeta wgrywa zdjęcia klikając punkt). Tylko dla MDCP. */}
      {order.order_type === "mdcp_geodezja" && (
        <div className="mt-8">
          <MdcpContractorMap orderId={order.id} readOnly={isPreview || isExecution} />
        </div>
      )}

      {/* Result files (pliki pozycji realizacyjnej) — contractor uploads, others review. */}
      <div className="mt-8">
        <OrderResultFilesSection orderId={order.id} />
      </div>

      {/* External costs — koszty to kwoty; „Realizacja" ich nie widzi ani nie zgłasza. */}
      {!isExecution && (
        <ExternalCostsSection orderId={order.id} allowExternalCosts={order.allow_external_costs} />
      )}

      </div>

      {/* Instrukcje dla podwykonawcy + komunikacja z zamawiającym (prawa kolumna) —
          taki sam układ jak w widoku GK (OrderDetailPage). */}
      <div className="mt-8 max-w-4xl lg:mt-0 lg:w-96 lg:shrink-0">
        <div className="lg:sticky lg:top-4">
          {/* Krótkie instrukcje (rozwijane) — jak dodać fakturę, zgłosić zmianę, złożyć POCK,
              korespondować, dodać zdjęcia mapy, pliki i skany zgód. Domyślnie rozwinięte
              w widoku podwykonawcy. */}
          <ContractorInstructionsPanel defaultOpen orderType={order.order_type} />
          {/* Comments & critical events; w podglądzie GK tylko do odczytu, żeby użytkownik
              wewnętrzny nie opublikował komentarza „z widoku podwykonawcy". */}
          <div ref={commentsRef} className="scroll-mt-4">
            <OrderCommentsSection orderId={order.id} readOnly={isPreview} />
          </div>
        </div>
      </div>
    </div>
  );
}
